import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';

/* ──────────────────────────────────────────────────────────────
   Cookie Consent — bottom-anchored banner shown on first visit.

   Behaviour:
   - Default state in index.html is `gtag('consent', 'default', { ... 'denied' })`,
     so Google Ads tracking is BLOCKED until the user accepts.
   - On Accept → writes `voqals.cookieConsent = 'accepted'` to localStorage,
     calls `gtag('consent', 'update', { ... 'granted' })`, and injects the
     gtag.js script tag.
   - On Reject → writes `voqals.cookieConsent = 'rejected'`. Tracking stays denied.
     gtag.js is never loaded.
   - On subsequent visits, if a decision was previously made, the banner does
     not re-appear. (To withdraw, the user clears cookies / localStorage —
     documented in the Privacy Policy.)

   The banner deliberately blocks no UI — it's anchored to the bottom and
   does not capture pointer events outside the panel itself.
   ────────────────────────────────────────────────────────────── */

const STORAGE_KEY = 'voqals.cookieConsent';
const GTAG_ID = 'AW-18066710209';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function injectGtagScript() {
  // Avoid double-injection if user clicks Accept multiple times.
  if (document.querySelector(`script[src*="gtag/js?id=${GTAG_ID}"]`)) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GTAG_ID}`;
  document.head.appendChild(s);
  window.gtag?.('config', GTAG_ID);
}

type Props = {
  onOpenPrivacy: () => void;
};

export default function CookieConsent({ onOpenPrivacy }: Props) {
  const [visible, setVisible] = useState(false);

  // On mount, decide whether to show the banner based on stored preference.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== 'accepted' && stored !== 'rejected') {
        // Defer slightly so the banner doesn't compete with hero LCP.
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch {
      // localStorage unavailable (e.g. private mode strict-blocking) — show
      // the banner so we always have explicit consent in this session.
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const handleAccept = () => {
    try { localStorage.setItem(STORAGE_KEY, 'accepted'); } catch { /* private mode */ }
    window.gtag?.('consent', 'update', {
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      analytics_storage: 'granted',
    });
    injectGtagScript();
    setVisible(false);
  };

  const handleReject = () => {
    try { localStorage.setItem(STORAGE_KEY, 'rejected'); } catch { /* private mode */ }
    // Consent stays at 'denied' (the index.html default). No gtag injection.
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-3 left-3 right-3 md:bottom-6 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[min(560px,calc(100vw-48px))] z-[9998] pointer-events-auto"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
        >
          <div className="rounded-2xl border border-white/15 bg-[#0a0a0a]/95 backdrop-blur-xl shadow-[0_24px_60px_rgba(0,0,0,0.5)] p-5 md:p-6">
            <h2 className="text-white text-sm md:text-[15px] font-bold tracking-tight mb-2">
              We use a few cookies
            </h2>
            <p className="text-white/65 text-[12px] md:text-[13px] leading-relaxed mb-4">
              Strictly-necessary cookies keep the site working. We'd also like to
              load Google Ads measurement so we can see whether our ads are
              effective. Nothing else. You can change your mind any time.
              {' '}
              <button
                onClick={onOpenPrivacy}
                className="underline decoration-white/30 underline-offset-2 hover:decoration-[#56CFFC] hover:text-white/85 transition-colors"
              >
                Read our Privacy Policy
              </button>
              .
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleReject}
                className="flex-1 px-4 py-2.5 rounded-full border border-white/15 text-white/75 text-[11px] md:text-[12px] font-bold tracking-[0.18em] uppercase hover:bg-white/5 hover:text-white hover:border-white/30 transition-all"
              >
                Reject
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 px-4 py-2.5 rounded-full bg-gradient-to-r from-[#56CFFC] to-[#1B36FF] text-white text-[11px] md:text-[12px] font-bold tracking-[0.18em] uppercase hover:scale-[1.02] hover:shadow-[0_0_24px_rgba(27,54,255,0.3)] transition-all"
              >
                Accept All
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
